/**
 * Access Requests admin page.
 *
 * Owner/Admin. Lists pending & processed external access requests
 * from /request-access, and lets you approve or reject with a scope
 * picker + optional reason.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Inbox, Loader2, CheckCircle2, XCircle, Building2, Mail, Phone,
  MessageSquare, ShieldCheck, Eye, Clock, X, Send,
} from 'lucide-react';
import { Card, Badge } from '../components/ui-custom/ComplianceUI';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_COLORS = {
  pending:  '#d97706',
  approved: '#059669',
  rejected: '#dc2626',
};

// Fixed expiry preset choices — matches the backend `expires_days` shortcut.
// `null` = permanent (no expiry).
const EXPIRY_PRESETS = [
  { value: null,  label: 'Permanent' },
  { value: 7,    label: '7 days' },
  { value: 14,   label: '14 days' },
  { value: 30,   label: '30 days' },
  { value: 90,   label: '90 days' },
  { value: 180,  label: '180 days' },
];


export default function AccessRequestsPage() {
  const [requests, setRequests] = useState([]);
  const [scopes, setScopes]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [busyId, setBusyId]     = useState(null);
  const [drafts, setDrafts]     = useState({}); // { reqId: { scopes: [] } }
  const [rejectReasons, setRejectReasons] = useState({});
  // Per-request expiry choice (null = permanent, number = days). Falls back
  // to `null` when nothing is set on the row.
  const [expiryDays, setExpiryDays] = useState({});
  // Modal state for the email preview
  const [emailPreview, setEmailPreview] = useState(null); // {html, to, subject, requestId, ...}
  const [previewLoadingId, setPreviewLoadingId] = useState(null);
  const [sendingTest, setSendingTest] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rRes, sRes] = await Promise.all([
        fetch(`${API_URL}/api/access-requests`, { credentials: 'include' }),
        fetch(`${API_URL}/api/access-control/scopes`, { credentials: 'include' }),
      ]);
      if (!rRes.ok || !sRes.ok) throw new Error('Failed to load');
      const rData = await rRes.json();
      const sData = await sRes.json();
      setRequests(rData.requests || []);
      setScopes(sData.scopes || []);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const getDraftScopes = (req) => {
    if (drafts[req.id]) return drafts[req.id].scopes;
    return req.requested_scopes || [];
  };

  const toggleScope = (req, key) => {
    const current = new Set(getDraftScopes(req));
    if (current.has(key)) current.delete(key);
    else current.add(key);
    setDrafts({ ...drafts, [req.id]: { scopes: Array.from(current) } });
  };

  const approve = async (req) => {
    setBusyId(req.id);
    try {
      const finalScopes = getDraftScopes(req);
      const body = { scopes: finalScopes };
      const days = expiryDays[req.id];
      if (typeof days === 'number' && days > 0) body.expires_days = days;

      const res = await fetch(
        `${API_URL}/api/access-requests/${req.id}/approve`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);

      // v1.25: signup flow — no temp password is generated. Just show a
      // success toast and refresh the list.
      toast.success(data.note || `Approved — ${data.email} can now sign in`);
      await load();
    } catch (err) {
      toast.error(`Approve failed: ${err.message}`);
    } finally {
      setBusyId(null);
    }
  };

  const previewEmailFor = async (req) => {
    setPreviewLoadingId(req.id);
    try {
      const scopeCsv = getDraftScopes(req).join(',');
      const days = expiryDays[req.id];
      const params = new URLSearchParams();
      if (scopeCsv) params.set('scopes', scopeCsv);
      if (typeof days === 'number' && days > 0) params.set('expires_days', String(days));
      const res = await fetch(
        `${API_URL}/api/access-requests/${req.id}/email-preview?${params.toString()}`,
        { credentials: 'include' },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      setEmailPreview({
        html:      data.html,
        to:        data.to,
        subject:   data.subject,
        scopes:    data.scopes,
        expiry:    data.scope_expires_at,
        days:      typeof days === 'number' ? days : null,
        requestId: req.id,
      });
    } catch (err) {
      toast.error(`Preview failed: ${err.message}`);
    } finally {
      setPreviewLoadingId(null);
    }
  };

  const sendTestEmail = async () => {
    if (!emailPreview) return;
    setSendingTest(true);
    try {
      const params = new URLSearchParams();
      if (emailPreview.scopes?.length) params.set('scopes', emailPreview.scopes.join(','));
      if (emailPreview.days) params.set('expires_days', String(emailPreview.days));
      const res = await fetch(
        `${API_URL}/api/access-requests/${emailPreview.requestId}/test-email?${params.toString()}`,
        { method: 'POST', credentials: 'include' },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      toast.success(`Sent to ${data.sent_to} — check your inbox`);
    } catch (err) {
      toast.error(`Send failed: ${err.message}`);
    } finally {
      setSendingTest(false);
    }
  };

  const reject = async (req) => {
    setBusyId(req.id);
    try {
      const res = await fetch(
        `${API_URL}/api/access-requests/${req.id}/reject`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ decision_reason: rejectReasons[req.id] || '' }),
        },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      toast.success('Rejected — requester has been notified');
      await load();
    } catch (err) {
      toast.error(`Reject failed: ${err.message}`);
    } finally {
      setBusyId(null);
    }
  };

  const filtered = statusFilter === 'all'
    ? requests
    : requests.filter((r) => r.status === statusFilter);

  const pendingCount = requests.filter((r) => r.status === 'pending').length;

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center' }}><Loader2 className="spin" size={24} /></div>;
  }

  return (
    <div className="access-requests-page" data-testid="access-requests-page">
      <div className="page-header">
        <div>
          <h1><Inbox size={24} /> Access Requests {pendingCount > 0 && (
            <Badge color="#d97706" data-testid="pending-count">{pendingCount} pending</Badge>
          )}</h1>
          <p>
            Self-registered Client Portal signups. The prospect chose their
            own password — you just pick the scopes and approve. They&apos;ll
            be forced through 2FA enrollment on their first successful sign-in.
          </p>
        </div>
      </div>

      {/* Status filter */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {['pending', 'approved', 'rejected', 'all'].map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            data-testid={`filter-${s}`}
            style={{
              padding: '6px 14px', borderRadius: 20,
              background: statusFilter === s ? '#2563eb' : 'rgba(255,255,255,0.05)',
              color: statusFilter === s ? '#fff' : '#cbd5e1',
              border: '1px solid rgba(255,255,255,0.10)', cursor: 'pointer',
              textTransform: 'capitalize', fontSize: 13,
            }}
          >
            {s}
          </button>
        ))}
      </div>

      {/* Request list */}
      {filtered.length === 0 ? (
        <Card style={{ padding: 40, textAlign: 'center' }} data-testid="requests-empty">
          <Inbox size={40} color="#6b7280" />
          <h3 style={{ marginTop: 12 }}>No {statusFilter} requests</h3>
        </Card>
      ) : (
        filtered.map((req) => {
          const draftScopes = getDraftScopes(req);
          const isBusy = busyId === req.id;
          const isPending = req.status === 'pending';
          return (
            <Card
              key={req.id}
              data-testid={`req-${req.id}`}
              style={{ padding: 20, marginBottom: 16 }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, marginBottom: 14 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                    <strong style={{ fontSize: 16 }}>{req.contact_name}</strong>
                    <Badge color={STATUS_COLORS[req.status] || '#6b7280'}>{req.status}</Badge>
                  </div>
                  <div style={{ fontSize: 13, color: '#94a3b8', display: 'flex', gap: 18, flexWrap: 'wrap' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <Building2 size={13} /> {req.company_name}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <Mail size={13} /> {req.contact_email}
                    </span>
                    {req.phone && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <Phone size={13} /> {req.phone}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                    Submitted {new Date(req.submitted_at).toLocaleString()}
                  </div>
                </div>
              </div>

              <div
                style={{
                  padding: 12, marginBottom: 14, borderRadius: 6,
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.06)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <MessageSquare size={14} color="#94a3b8" style={{ marginTop: 3, flexShrink: 0 }} />
                  <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5, color: '#cbd5e1' }}>
                    {req.reason}
                  </p>
                </div>
              </div>

              {isPending && (
                <>
                  <h4 style={{ margin: '0 0 8px 0', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#cbd5e1' }}>
                    <ShieldCheck size={14} /> Grant access to
                    <span style={{ color: '#94a3b8', fontWeight: 400 }}>
                      (requested: {(req.requested_scopes || []).join(', ') || 'nothing specified'})
                    </span>
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8, marginBottom: 14 }}>
                    {scopes.map((s) => {
                      const on = draftScopes.includes(s.key);
                      return (
                        <label
                          key={s.key}
                          data-testid={`req-scope-choice-${req.id}-${s.key}`}
                          style={{
                            display: 'flex', gap: 8, padding: 8, borderRadius: 6, cursor: 'pointer',
                            background: on ? 'rgba(37,99,235,0.10)' : 'rgba(255,255,255,0.03)',
                            border: `1px solid ${on ? 'rgba(37,99,235,0.35)' : 'rgba(255,255,255,0.08)'}`,
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={on}
                            onChange={() => toggleScope(req, s.key)}
                            style={{ marginTop: 2 }}
                          />
                          <span>
                            <div style={{ fontSize: 12, fontWeight: 600 }}>{s.label}</div>
                          </span>
                        </label>
                      );
                    })}
                  </div>

                  {/* Expiry selector */}
                  <div
                    style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}
                    data-testid={`req-expiry-${req.id}`}
                  >
                    <Clock size={14} color="#94a3b8" />
                    <span style={{ fontSize: 12, color: '#cbd5e1' }}>Access expires:</span>
                    {EXPIRY_PRESETS.map((p) => {
                      const active = (expiryDays[req.id] ?? null) === p.value;
                      return (
                        <button
                          key={String(p.value)}
                          type="button"
                          onClick={() => setExpiryDays({ ...expiryDays, [req.id]: p.value })}
                          data-testid={`expiry-${req.id}-${p.value ?? 'permanent'}`}
                          style={{
                            padding: '4px 10px', borderRadius: 999, fontSize: 12,
                            cursor: 'pointer',
                            background: active ? '#2563eb' : 'rgba(255,255,255,0.05)',
                            color: active ? '#fff' : '#cbd5e1',
                            border: '1px solid rgba(255,255,255,0.15)',
                          }}
                        >
                          {p.label}
                        </button>
                      );
                    })}
                  </div>

                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => approve(req)}
                      disabled={isBusy || draftScopes.length === 0}
                      data-testid={`approve-${req.id}`}
                      title={draftScopes.length === 0 ? 'Pick at least one scope' : 'Approve and provision account'}
                      style={{
                        padding: '9px 18px', borderRadius: 6,
                        background: draftScopes.length === 0 ? '#334155' : '#059669',
                        color: '#fff', border: 'none',
                        cursor: (isBusy || draftScopes.length === 0) ? 'not-allowed' : 'pointer',
                        display: 'flex', alignItems: 'center', gap: 6,
                      }}
                    >
                      {isBusy ? <Loader2 size={14} className="spin" /> : <CheckCircle2 size={14} />}
                      Approve &amp; provision
                    </button>

                    <button
                      onClick={() => previewEmailFor(req)}
                      disabled={previewLoadingId === req.id || draftScopes.length === 0}
                      data-testid={`preview-email-${req.id}`}
                      title="Preview the exact email the requester will receive"
                      style={{
                        padding: '9px 14px', borderRadius: 6,
                        background: 'transparent',
                        color: '#60a5fa',
                        border: '1px solid rgba(96,165,250,0.4)',
                        cursor: (previewLoadingId === req.id) ? 'wait' : 'pointer',
                        display: 'flex', alignItems: 'center', gap: 6,
                      }}
                    >
                      {previewLoadingId === req.id
                        ? <Loader2 size={14} className="spin" />
                        : <Eye size={14} />}
                      Preview email
                    </button>

                    <input
                      type="text"
                      value={rejectReasons[req.id] || ''}
                      onChange={(e) => setRejectReasons({ ...rejectReasons, [req.id]: e.target.value })}
                      placeholder="(optional) Reason to include in the rejection email"
                      data-testid={`reject-reason-${req.id}`}
                      style={{
                        flex: 1, minWidth: 240, padding: '8px 12px', borderRadius: 6,
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.15)', color: 'inherit', fontSize: 13,
                      }}
                    />
                    <button
                      onClick={() => reject(req)}
                      disabled={isBusy}
                      data-testid={`reject-${req.id}`}
                      style={{
                        padding: '9px 18px', borderRadius: 6,
                        background: 'transparent',
                        color: '#f87171', border: '1px solid rgba(248,113,113,0.4)',
                        cursor: isBusy ? 'wait' : 'pointer',
                        display: 'flex', alignItems: 'center', gap: 6,
                      }}
                    >
                      <XCircle size={14} /> Reject
                    </button>
                  </div>
                </>
              )}

              {!isPending && (
                <div style={{ fontSize: 12, color: '#94a3b8' }}>
                  {req.status === 'approved' && req.provisioned_email && (
                    <div style={{ marginBottom: 4 }}>
                      Provisioned <b>{req.provisioned_email}</b>
                      {req.granted_scopes?.length > 0 && (
                        <> with scopes: {req.granted_scopes.map((s) => {
                          const exp = req.scope_expires_at?.[s];
                          return (
                            <Badge
                              key={s}
                              color={exp ? '#d97706' : '#059669'}
                              style={{ marginLeft: 4 }}
                              data-testid={`granted-scope-${req.id}-${s}`}
                            >
                              {s}
                              {exp ? ` · until ${new Date(exp).toLocaleDateString()}` : ''}
                            </Badge>
                          );
                        })}</>
                      )}
                    </div>
                  )}
                  {req.status === 'rejected' && req.decision_reason && (
                    <div>Reason: <i>{req.decision_reason}</i></div>
                  )}
                  <div>reviewed by {req.reviewed_by || '(unknown)'} on {new Date(req.reviewed_at).toLocaleString()}</div>
                </div>
              )}
            </Card>
          );
        })
      )}

      {/* Email Preview modal */}
      {emailPreview && (
        <div
          data-testid="email-preview-modal"
          onClick={() => setEmailPreview(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 50,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#0f172a', borderRadius: 10,
              width: '95%', maxWidth: 780, maxHeight: '92vh',
              display: 'flex', flexDirection: 'column',
              border: '1px solid rgba(255,255,255,0.10)',
            }}
          >
            <div
              style={{
                padding: 16, borderBottom: '1px solid rgba(255,255,255,0.08)',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: 12, flexWrap: 'wrap',
              }}
            >
              <div style={{ fontSize: 14, minWidth: 240 }}>
                <div style={{ color: '#94a3b8', fontSize: 11, marginBottom: 2 }}>PREVIEW OF APPROVAL EMAIL</div>
                <div><b>To:</b> {emailPreview.to}</div>
                <div><b>Subject:</b> {emailPreview.subject}</div>
                {emailPreview.days
                  ? <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 4 }}>Scopes expire in {emailPreview.days} day{emailPreview.days === 1 ? '' : 's'}</div>
                  : emailPreview.scopes?.length
                    ? <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 4 }}>Scopes are permanent</div>
                    : null}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button
                  onClick={sendTestEmail}
                  disabled={sendingTest}
                  data-testid="send-test-email-btn"
                  title="Send this email preview to your own inbox — nothing is provisioned"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '9px 14px', borderRadius: 6,
                    background: '#2563eb', color: '#fff',
                    border: 'none', cursor: sendingTest ? 'wait' : 'pointer',
                    fontWeight: 600, fontSize: 13,
                  }}
                >
                  {sendingTest ? <Loader2 size={14} className="spin" /> : <Send size={14} />}
                  Send test to my inbox
                </button>
                <button
                  onClick={() => setEmailPreview(null)}
                  data-testid="email-preview-close"
                  style={{
                    background: 'transparent', border: 'none', color: '#94a3b8',
                    cursor: 'pointer', padding: 4, display: 'flex',
                  }}
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            <iframe
              title="Email preview"
              srcDoc={emailPreview.html}
              data-testid="email-preview-iframe"
              style={{
                flex: 1, width: '100%', border: 'none',
                background: '#fff', borderBottomLeftRadius: 10, borderBottomRightRadius: 10,
                minHeight: 500,
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
